import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {faCalendar} from '@fortawesome/free-solid-svg-icons';
import {UntypedFormControl} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {Orders} from '../../models/order-model';
import {Router} from '@angular/router';
import {Employees} from '../../models/employees-model';
import {LocalStorageService} from '../../../local-storage.service';
import {Kostenstelle} from '../../models/kostenstelle-model';
import {EmptyCostCenter} from '../../models/timeControlEntry';
import {DataService} from '../../services/data.service';
import {UserAuthenticationService} from '../../../services/user-authentication.service';

@Component({
    selector: 'app-auftrag-dialog', templateUrl: './auftrag-dialog.component.html', styleUrls: ['./auftrag-dialog.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuftragDialogComponent implements OnInit, AfterViewInit {
    faCalendar = faCalendar;
    formValue = new UntypedFormControl('');
    public orders!: Orders[];
    filteredOptions!: Observable<Orders[]>;
    filteredOptionSize: BehaviorSubject<number> = new BehaviorSubject(0);
    selectedOrder!: Orders;
    public userPermissions!: Employees;
    public kostenStelleModel!: Kostenstelle[];
    @ViewChild('input', {static: true}) inputField?: ElementRef<HTMLInputElement>;
    public displayShareOrderBtn = true;
    protected readonly Math = Math;
    private maxNROfSearchResults = 30;

    constructor(public dialogRef: MatDialogRef<AuftragDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public componentData: any,
                private router: Router,
                private localStorage: LocalStorageService,
                private dataService: DataService,
                private userAuthService: UserAuthenticationService) {
    }

    ngOnInit(): void {
        if (!this.componentData.position?.top) {
            this.dialogRef.updatePosition({top: '3.75rem'});
        }
        this.orders = this.componentData.data.orders;
        this.userPermissions = this.componentData.data.userPermissions;
        this.filteredOptions = this.formValue.valueChanges.pipe(startWith(''), map((value) => this._filter(value)));
        this.displayShareOrderBtn = !!this.userAuthService.getCurrentAuthenticatedUser()?.orderSourceVinavonTime;
        this.formValue.setValue(this.componentData.data.selectedOrder.objectSummary);
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.inputField?.nativeElement.focus(), 100);
    }

    async shareAuftrag(): Promise<void> {
        const selectedOrder = this.orders.find((el) => el.objectNumber === this.componentData.data.selectedOrder.objectNumber);
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (user) {
            if (user.orderSourceVinavonTime === 1) /* Auftrag von Einsatzplanung */ {
                await this.router.navigateByUrl('/timeapp/share-order');
            } else if (user.orderSourceVinavonTime === 2) {  /* Auftrag von Auslastungsplanung */
                await this.router.navigateByUrl('/timeapp/share-emp-order');
            }

        }
        this.dialogRef.close({value: selectedOrder});
    }

    async selectedValue(item: Orders): Promise<void> {
        if (!item) {
            return;
        }
        const timeEntry = await this.localStorage.get('currentTimeEntry', 'SwissTimeEntry');
        const costCenterByObjectID = await this.dataService.GetCostCentersByObjectID2(item.objectNumber, item.status, this.componentData.data.costs);
        if (costCenterByObjectID) {
            this.kostenStelleModel = costCenterByObjectID;
        }

        if (this.kostenStelleModel.length === 0) {
            const newData = timeEntry;
            newData.costs = EmptyCostCenter();
            await this.localStorage.add('currentTimeEntry', newData, 'SwissTimeEntry');
        }

        this.selectedOrder = item;
        this.next();
    }

    next(): void {
        let isPseudoOrder = false;
        if (!this.selectedOrder) {
            isPseudoOrder = true;
            this.selectedOrder = {
                objectNumber: this.formValue.value,
                objectID: '',
                description: '',
                objectSummary: '',
                lastname: '',
                firstname: '',
                street1: '',
                city: '',
                zip: 0,
                phone: '',
                phone03: '',
                anspLastname: '',
                anspFirstname: '',
                anspPhone: '',
                anspPhone03: '',
                archLastname: '',
                archFirstname: '',
                archPhone: '',
                archPhone03: '',
                archStreet1: '',
                archCity: '',
                archZip: '',
                archAnspLastname: '',
                archAnspFirstname: '',
                archAnspPhone: '',
                archAnspPhone03: '',
                note4: '',
                dateOfDelivery: '',
                dateOfDeliveryOK: 0,
                orderDay: null,
                startTime: null,
                endTime: null,
                notes: null,
                fK_AutoCostCenter: 0,
                employeeID: 0,
                liefLastname: '',
                liefFirstname: '',
                liefPhone: '',
                liefPhone03: '',
                liefStreet1: '',
                liefCity: '',
                liefZip: '',
                liefAnspLastname: '',
                liefAnspFirstname: '',
                liefAnspPhone: '',
                liefAnspPhone03: '',
                status: 0,
                recomendedWorkPlace: '',
            };
        }
        this.dialogRef.close({value: this.selectedOrder, isPseudoOrder});
    }

    // Umwandlung CRLF (\r\n) zu Zeilenumbruch
    protected getItemDescription(value: string): string {
        return value?.replace('\r\n', '<br />');
    }

    private _filter(value: string): Orders[] {
        if (!value) {
            this.filteredOptionSize.next(Math.min(this.orders.length, 5) * 50);
            return this.orders.slice(0, 30);
        }

        const filterValue = value.toLowerCase();
        const oderSearchString = this.userPermissions.orderSearchString || '';
        let searchResults: Orders[] = [];
        if (oderSearchString.length > 0) {
            searchResults = this.orders.filter((option) => Object.values(option[oderSearchString])
                .toString()
                .toLowerCase()
                .includes(filterValue));
        } else {
            searchResults = this.orders.filter((option) => {
                // 31.01.23/TH: Quelle für die Suchfelder: Alte Zeiterfassungs-App (Activity1.cs => RefreshOrders())
                return (option.objectNumber.toLowerCase().startsWith(filterValue) ||
                    (option.note4 + ' ' +
                        option.objectSummary + ' ' +
                        option.lastname + ' ' +
                        option.firstname + ' ' +
                        option.street1 + ' ' +
                        option.zip + ' ' +
                        option.city + ' ' +
                        option.archLastname + ' ' +
                        option.archFirstname + ' ' +
                        option.archZip + ' ' +
                        option.archCity
                    ).toLowerCase().includes(filterValue));
            });
        }
        this.filteredOptionSize.next(Math.min(searchResults.length, 5) * 50);
        return searchResults.slice(0, this.maxNROfSearchResults);
    }
}
